import type { ChatStreamEvent } from "@/types/api";

const API_BASE = import.meta.env.VITE_API_BASE ?? "";

export type StreamHandlers = {
  onEvent: (event: ChatStreamEvent) => void;
  onError?: (err: Error) => void;
};

/**
 * Streams /api/chat as Server-Sent Events. Each event is a JSON payload
 * matching ChatStreamEvent. The connection closes after `{ kind: "done" }`.
 */
export async function streamChat(
  question: string,
  signal: AbortSignal,
  handlers: StreamHandlers,
): Promise<void> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/chat`, {
      method: "POST",
      credentials: "include",
      signal,
      headers: { "Content-Type": "application/json", Accept: "text/event-stream" },
      body: JSON.stringify({ question }),
    });
  } catch (err) {
    handlers.onError?.(err as Error);
    return;
  }

  if (!res.ok || !res.body) {
    handlers.onError?.(new Error(`Chat failed: ${res.status} ${res.statusText}`));
    return;
  }

  const reader = res.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) break;
      buffer += value;

      let sep: number;
      while ((sep = buffer.indexOf("\n\n")) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);

        const dataLine = frame
          .split("\n")
          .find((l) => l.startsWith("data:"));
        if (!dataLine) continue;

        const json = dataLine.slice(5).trim();
        if (!json) continue;

        try {
          const event = JSON.parse(json) as ChatStreamEvent;
          handlers.onEvent(event);
          if (event.kind === "done") return;
        } catch (err) {
          handlers.onError?.(new Error(`Bad SSE payload: ${(err as Error).message}`));
        }
      }
    }
  } catch (err) {
    if ((err as Error).name !== "AbortError") {
      handlers.onError?.(err as Error);
    }
  }
}
